import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Edit2, Search, Receipt } from 'lucide-react';
import { primaryButton, ghostButton, inputStyle, glass, glassLight, T, StatCard, EmptyState, Field } from '../ui';
interface Cliente {
  id: string;
  nombre: string;
  codigo?: string;
}
interface Venta {
  id: string;
  numeroFactura: string;
  clienteId: string;
  clienteNombre: string;
  fecha: string;
  cantidad: number;
  precio: number;
  total: number;
  ruta: string;
  supervisor: string;
  observaciones: string;
}
interface VentasProps {
  ventas: Venta[];
  clientes: Cliente[];
  onAddVenta: (venta: Venta) => void;
  onDeleteVenta: (id: string) => void;
  onUpdateVenta: (venta: Venta) => void;
}
const iconBtn: React.CSSProperties = {
  width: 30,
  height: 30,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  border: `1px solid ${T.border}`,
  background: T.surface,
  cursor: 'pointer'
};
function fmtRD(n: number) {
  return new Intl.NumberFormat('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(n);
}
export function Ventas({
  ventas,
  clientes,
  onAddVenta,
  onDeleteVenta,
  onUpdateVenta
}: VentasProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFecha, setFilterFecha] = useState('');
  const [formData, setFormData] = useState<Partial<Venta>>({
    cantidad: 0,
    precio: 0,
    total: 0
  });
  const [selectedCliente, setSelectedCliente] = useState<string>('');
  const handleCalculateTotal = (cantidad: number, precio: number) => {
    const total = cantidad * precio;
    setFormData(prev => ({
      ...prev,
      cantidad,
      precio,
      total
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.numeroFactura || !selectedCliente || !formData.fecha || !formData.cantidad || !formData.precio) {
      alert('Por favor completa los campos obligatorios');
      return;
    }
    const cliente = clientes.find(c => c.id === selectedCliente);
    if (editingId) {
      onUpdateVenta({
        ...formData,
        id: editingId,
        clienteId: selectedCliente,
        clienteNombre: cliente?.nombre || ''
      } as Venta);
      setEditingId(null);
    } else {
      onAddVenta({
        ...formData,
        id: Date.now().toString(),
        clienteId: selectedCliente,
        clienteNombre: cliente?.nombre || ''
      } as Venta);
    }
    setFormData({
      cantidad: 0,
      precio: 0,
      total: 0
    });
    setSelectedCliente('');
    setShowForm(false);
  };
  const handleEdit = (venta: Venta) => {
    setFormData(venta);
    setSelectedCliente(venta.clienteId);
    setEditingId(venta.id);
    setShowForm(true);
  };
  const filteredVentas = ventas.filter(v => {
    const matchSearch = v.numeroFactura.toLowerCase().includes(searchTerm.toLowerCase()) || v.clienteNombre.toLowerCase().includes(searchTerm.toLowerCase());
    const matchFecha = !filterFecha || v.fecha === filterFecha;
    return matchSearch && matchFecha;
  });
  const ventasHoy = ventas.filter(v => v.fecha === new Date().toISOString().split('T')[0]).length;
  const totalVentas = ventas.reduce((sum, v) => sum + v.total, 0);
  const totalCantidad = ventas.reduce((sum, v) => sum + v.cantidad, 0);
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: 20
  }} data-id="element-934">
      {/* Header Stats */}
      <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: 14
    }} data-id="element-935">
        
        <StatCard label="Total ventas" value={String(ventas.length)} note="Facturas registradas" accent={T.info} data-id="element-936" />
        
        <StatCard label="Hoy" value={String(ventasHoy)} note="Facturadas en el día" accent={T.ok} data-id="element-937" />
        
        <StatCard label="Monto total" value={`RD$ ${fmtRD(totalVentas)}`} note="Acumulado histórico" accent={T.warn} data-id="element-938" />
        
        <StatCard label="Cantidad total" value={`${totalCantidad.toFixed(0)} gal`} note="Volumen distribuido" accent="#8B5CF6" data-id="element-939" />
        
      </div>

      {/* Controles */}
      <div style={{
      ...glassLight,
      padding: 16
    }} data-id="element-940">
        <div style={{
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        alignItems: 'center'
      }} data-id="element-941">
          
          <div style={{
          flex: '1 1 240px',
          position: 'relative'
        }} data-id="element-942">
            <Search size={16} style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: T.textMuted
          }} data-id="element-943" />
            
            <input type="text" placeholder="Buscar por factura o cliente..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{
            ...inputStyle,
            paddingLeft: 36
          }} data-id="element-944" />
            
          </div>
          <input type="date" value={filterFecha} onChange={e => setFilterFecha(e.target.value)} style={{
          ...inputStyle,
          width: 'auto'
        }} data-id="element-945" />
          
          <button onClick={() => {
          setShowForm(!showForm);
          setEditingId(null);
          setFormData({
            cantidad: 0,
            precio: 0,
            total: 0
          });
          setSelectedCliente('');
        }} style={{
          ...primaryButton,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6
        }} data-id="element-946">
            
            <Plus size={15} data-id="element-947" /> Registrar Venta
          </button>
        </div>
      </div>

      {/* Formulario */}
      <AnimatePresence data-id="element-948">
        {showForm && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2
      }} style={{
        overflow: 'hidden'
      }} data-id="element-949">
          
            <div style={{
          ...glass,
          padding: 20
        }} data-id="element-950">
              <h3 style={{
            fontSize: 15,
            fontWeight: 800,
            marginBottom: 16,
            color: T.text
          }} data-id="element-951">
              
                {editingId ? 'Editar Venta' : 'Registrar Nueva Venta'}
              </h3>
              <form onSubmit={handleSubmit} data-id="element-952">
                <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 14
            }} data-id="element-953">
                
                  <Field label="Número de factura *" data-id="element-954">
                    <input type="text" value={formData.numeroFactura || ''} onChange={e => setFormData({
                  ...formData,
                  numeroFactura: e.target.value
                })} style={inputStyle} placeholder="FAC-2024-0001" data-id="element-955" />
                  
                  </Field>
                  <Field label="Fecha *" data-id="element-956">
                    <input type="date" value={formData.fecha || ''} onChange={e => setFormData({
                  ...formData,
                  fecha: e.target.value
                })} style={inputStyle} data-id="element-957" />
                  
                  </Field>
                  <Field label="Cliente *" data-id="element-958">
                    <select value={selectedCliente} onChange={e => setSelectedCliente(e.target.value)} style={inputStyle} data-id="element-959">
                    
                      <option value="" data-id="element-960">Selecciona un cliente</option>
                      {clientes.map(c => <option key={c.id} value={c.id} data-id="element-961">
                          {c.nombre}{c.codigo ? ` (${c.codigo})` : ''}
                        </option>)}
                    </select>
                  </Field>
                  <Field label="Ruta" data-id="element-962">
                    <input type="text" value={formData.ruta || ''} onChange={e => setFormData({
                  ...formData,
                  ruta: e.target.value
                })} style={inputStyle} placeholder="Ruta Centro" data-id="element-963" />
                  
                  </Field>
                  <Field label="Cantidad (galones) *" data-id="element-964">
                    <input type="number" step="0.01" value={formData.cantidad || 0} onChange={e => handleCalculateTotal(parseFloat(e.target.value), formData.precio || 0)} style={inputStyle} placeholder="0" data-id="element-965" />
                  
                  </Field>
                  <Field label="Precio por galón *" data-id="element-966">
                    <input type="number" step="0.01" value={formData.precio || 0} onChange={e => handleCalculateTotal(formData.cantidad || 0, parseFloat(e.target.value))} style={inputStyle} placeholder="0.00" data-id="element-967" />
                  
                  </Field>
                  <Field label="Total (RD$)" data-id="element-968">
                    <input type="number" value={formData.total || 0} readOnly style={{
                  ...inputStyle,
                  opacity: 0.6,
                  fontWeight: 700,
                  color: T.ok
                }} data-id="element-969" />
                  
                  </Field>
                  <Field label="Supervisor" data-id="element-970">
                    <input type="text" value={formData.supervisor || ''} onChange={e => setFormData({
                  ...formData,
                  supervisor: e.target.value
                })} style={inputStyle} placeholder="Nombre del supervisor" data-id="element-971" />
                  
                  </Field>
                  <div style={{
                gridColumn: '1 / -1'
              }} data-id="element-972">
                    <Field label="Observaciones" data-id="element-973">
                      <textarea value={formData.observaciones || ''} onChange={e => setFormData({
                    ...formData,
                    observaciones: e.target.value
                  })} style={{
                    ...inputStyle,
                    resize: 'vertical'
                  }} rows={3} placeholder="Notas adicionales..." data-id="element-974" />
                    
                    </Field>
                  </div>
                </div>
                <div style={{
              display: 'flex',
              gap: 8,
              marginTop: 16
            }} data-id="element-975">
                  <button type="submit" style={primaryButton} data-id="element-976">
                    {editingId ? 'Actualizar' : 'Registrar'} venta
                  </button>
                  <button type="button" onClick={() => {
                setShowForm(false);
                setEditingId(null);
              }} style={ghostButton} data-id="element-977">
                  
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Tabla de Ventas */}
      <div style={{
      ...glass,
      padding: 20
    }} data-id="element-978">
        <h3 style={{
        fontSize: 15,
        fontWeight: 800,
        marginBottom: 16,
        color: T.text
      }} data-id="element-979">
          
          Registro de Ventas ({filteredVentas.length})
        </h3>
        {filteredVentas.length === 0 ? <EmptyState icon={<Receipt size={20} data-id="element-981" />} title="No hay ventas registradas" hint="Registra una venta nueva o ajusta los filtros de búsqueda." data-id="element-980" /> : <>
            <div style={{
          overflowX: 'auto'
        }} data-id="element-982">
              <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 13
          }} data-id="element-983">
              
                <thead data-id="element-984">
                  <tr data-id="element-985">
                    {[{
                  h: 'Factura',
                  a: 'left'
                }, {
                  h: 'Fecha',
                  a: 'left'
                }, {
                  h: 'Cliente',
                  a: 'left'
                }, {
                  h: 'Cantidad (gal)',
                  a: 'right'
                }, {
                  h: 'Precio',
                  a: 'right'
                }, {
                  h: 'Total (RD$)',
                  a: 'right'
                }, {
                  h: 'Ruta',
                  a: 'left'
                }, {
                  h: 'Supervisor',
                  a: 'left'
                }, {
                  h: 'Acciones',
                  a: 'center'
                }].map(({
                  h,
                  a
                }) => <th key={h} style={{
                  textAlign: a as any,
                  padding: '0 10px 10px',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: T.textMuted,
                  borderBottom: `1px solid ${T.border}`
                }} data-id="element-986">
                    
                        {h}
                      </th>)}
                  </tr>
                </thead>
                <tbody data-id="element-987">
                  {[...filteredVentas].sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime()).map(venta => <tr key={venta.id} data-id="element-988">
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  fontFamily: 'monospace',
                  color: T.info,
                  fontWeight: 700
                }} data-id="element-989">
                    
                        {venta.numeroFactura}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  color: T.textSecondary
                }} data-id="element-990">
                    
                        {venta.fecha}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  fontWeight: 600,
                  color: T.text
                }} data-id="element-991">
                    
                        {venta.clienteNombre}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  textAlign: 'right',
                  color: T.textSecondary
                }} data-id="element-992">
                    
                        {venta.cantidad.toFixed(2)}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  textAlign: 'right',
                  color: T.textSecondary
                }} data-id="element-993">
                    
                        {venta.precio.toFixed(2)}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  textAlign: 'right',
                  fontWeight: 800,
                  color: T.ok
                }} data-id="element-994">
                    
                        {venta.total.toFixed(2)}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  color: T.textSecondary
                }} data-id="element-995">
                    
                        {venta.ruta || '-'}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  color: T.textSecondary
                }} data-id="element-996">
                    
                        {venta.supervisor || '-'}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  textAlign: 'center'
                }} data-id="element-997">
                    
                        <div style={{
                    display: 'inline-flex',
                    gap: 6,
                    justifyContent: 'center'
                  }} data-id="element-998">
                      
                          <button onClick={() => handleEdit(venta)} title="Editar" style={{
                      ...iconBtn,
                      color: T.info
                    }} data-id="element-999">
                        
                            <Edit2 size={14} data-id="element-1000" />
                          </button>
                          <button onClick={() => onDeleteVenta(venta.id)} title="Eliminar" style={{
                      ...iconBtn,
                      color: T.danger
                    }} data-id="element-1001">
                        
                            <Trash2 size={14} data-id="element-1002" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* Resumen */}
            <div style={{
          borderTop: `1px solid ${T.border}`,
          marginTop: 18,
          paddingTop: 18,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 14
        }} data-id="element-1003">
            
              <div data-id="element-1004">
                <div style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: T.textMuted,
              marginBottom: 4
            }} data-id="element-1005">
                
                  Registros
                </div>
                <div style={{
              fontSize: 18,
              fontWeight: 800,
              color: T.info
            }} data-id="element-1006">
                  {filteredVentas.length}
                </div>
              </div>
              <div data-id="element-1007">
                <div style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: T.textMuted,
              marginBottom: 4
            }} data-id="element-1008">
                
                  Total galones
                </div>
                <div style={{
              fontSize: 18,
              fontWeight: 800,
              color: '#8B5CF6'
            }} data-id="element-1009">
                  {filteredVentas.reduce((sum, v) => sum + v.cantidad, 0).toFixed(2)}
                </div>
              </div>
              <div data-id="element-1010">
                <div style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: T.textMuted,
              marginBottom: 4
            }} data-id="element-1011">
                
                  Monto total
                </div>
                <div style={{
              fontSize: 18,
              fontWeight: 800,
              color: T.ok
            }} data-id="element-1012">
                  RD$ {filteredVentas.reduce((sum, v) => sum + v.total, 0).toFixed(2)}
                </div>
              </div>
              <div data-id="element-1013">
                <div style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: T.textMuted,
              marginBottom: 4
            }} data-id="element-1014">
                
                  Precio promedio
                </div>
                <div style={{
              fontSize: 18,
              fontWeight: 800,
              color: T.warn
            }} data-id="element-1015">
                  {(filteredVentas.reduce((sum, v) => sum + v.precio, 0) / filteredVentas.length).toFixed(2)}
                </div>
              </div>
            </div>
          </>}
      </div>
    </div>;
}